import { type PermissionType, PermissionTypeCreateSchema, type PermissionTypeUpdate, PermissionTypeUpdateSchema } from '@gestion-permisos/contracts';
import { Plus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ADMIN_PAGE_SIZE, useAdminTypesPage, useCreateType, useUpdateType } from '../admin/admin-api.js';
import {
  BackToAdmin,
  Checkbox,
  describedBy,
  Field,
  firstForbidden,
  INPUT,
  isStale,
  Notice,
  nullIfBlank,
} from '../admin/admin-ui.js';
import { ApiErrorView } from '../components/api-error-view.js';
import { buttonClasses } from '../components/button-styles.js';
import { Card } from '../components/card.js';
import { Modal, ModalFooter } from '../components/modal.js';
import { PageHeader } from '../components/page-header.js';
import { ProblemMessage } from '../components/problem-message.js';
import { pageFrom, Pagination, PastLastPage, TABLE, TD, TH, usePageHref } from '../requests/request-list.js';

type TypeErrors = { code?: string; name?: string; description?: string; requiresDocument?: string };

const MESSAGES: Required<TypeErrors> = {
  code: 'Código de 1 a 50 caracteres: letras, números, «_» o «-».',
  name: 'Escribe un nombre (hasta 200 caracteres).',
  description: 'La descripción admite hasta 2000 caracteres.',
  requiresDocument: '«Requiere documento» exige marcar también «Permite documento».',
};

/** Errores de forma del esquema del contrato, por campo, con texto fijo. */
function shapeErrors(issues: { path: PropertyKey[] }[]): TypeErrors {
  const errors: TypeErrors = {};
  for (const issue of issues) {
    const field = issue.path[0];
    if (field === 'code' || field === 'name' || field === 'description' || field === 'requiresDocument') errors[field] = MESSAGES[field];
  }
  return errors;
}

const documentLabel = (type: PermissionType) => (type.requiresDocument ? 'Requerido' : type.allowsDocument ? 'Opcional' : 'No permitido');

/** Botones de esta pantalla, con el aspecto de las ya adaptadas (`admin-ui.tsx` conserva los suyos). */
const PRIMARY = buttonClasses();
const OUTLINE = buttonClasses('outline');

/**
 * Tipos de permiso (W7): todos, también los inactivos (`GET /permission-types` sin `active`); crear (`POST`)
 * y editar o desactivar (`PATCH` con `If-Match`, solo lo cambiado). «Requiere documento» sin «Permite
 * documento» no llega a la API (lo rechaza el esquema del contrato). Un 409 se muestra tal cual; un 412
 * vuelve a leer el listado y el formulario se recarga con la versión actual.
 *
 * Con el aspecto de la Power App: cabecera, la tabla en una tarjeta con «Crear» en su cabecera, y el alta y
 * la edición en ventanas (`Modal`). Cerrar una ventana no envía nada.
 */
export function AdminTypesPage() {
  const [params] = useSearchParams();
  const page = pageFrom(params, 'pagina');
  const pageHref = usePageHref('pagina');
  const types = useAdminTypesPage(page);
  const create = useCreateType();
  const update = useUpdateType();
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  const [saved, setSaved] = useState<string | undefined>(undefined);
  // El tipo en edición sale del listado: tras un 412 el listado se vuelve a leer y llega la versión actual.
  const editing = types.data?.items.find((type) => type.id === editingId);

  const denied = firstForbidden(types.error, create.error, update.error);
  if (denied) return <ApiErrorView error={denied} />;

  const openCreate = () => {
    // Un error de un alta anterior no se arrastra a la nueva.
    create.reset();
    setSaved(undefined);
    setCreating(true);
  };

  return (
    <div className="space-y-7 lg:px-6 lg:py-4">
      <BackToAdmin />
      <PageHeader title="Tipos de permiso" description="Crear, editar y desactivar tipos de permiso." />
      <Notice text={saved} />

      <div>
        {types.isPending ? <output className="block text-muted-foreground">Cargando…</output> : null}
        {types.isError ? <ApiErrorView error={types.error} /> : null}
        {types.isSuccess ? (
          <>
            <Card flush>
              <div className="flex justify-end border-b border-border p-4">
                <button type="button" className={PRIMARY} onClick={openCreate}>
                  <Plus aria-hidden className="size-4" />
                  Crear
                </button>
              </div>
              {types.data.total === 0 ? (
                <p className="px-5 py-16 text-center text-sm text-muted-foreground">Todavía no hay tipos de permiso.</p>
              ) : types.data.items.length === 0 ? (
                <div className="p-5">
                  <PastLastPage firstPage={pageHref(1)} what="tipos" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className={TABLE} aria-label="Tipos de permiso">
                    <thead className="border-b border-border bg-muted text-muted-foreground">
                      <tr>
                        <th scope="col" className={TH}>Código</th>
                        <th scope="col" className={TH}>Nombre</th>
                        <th scope="col" className={TH}>Documento</th>
                        <th scope="col" className={TH}>Estado</th>
                        <th scope="col" className={TH}>
                          <span className="sr-only">Acciones</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {types.data.items.map((type) => (
                        <tr key={type.id} className="border-t border-border first:border-t-0">
                          <td className={TD}>{type.code}</td>
                          <th scope="row" className={TH}>{type.name}</th>
                          <td className={TD}>{documentLabel(type)}</td>
                          <td className={TD}>{type.active ? 'Activo' : 'Inactivo'}</td>
                          <td className={TD}>
                            <button
                              type="button"
                              className={OUTLINE}
                              aria-label={`Editar ${type.name}`}
                              onClick={() => {
                                update.reset();
                                setSaved(undefined);
                                setEditingId(type.id);
                              }}
                            >
                              Editar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
            {types.data.total > 0 ? (
              <Pagination page={page} total={types.data.total} pageSize={ADMIN_PAGE_SIZE} hrefFor={pageHref} units={['tipo', 'tipos']} />
            ) : null}
          </>
        ) : null}
      </div>

      {creating ? (
        <Modal title="Nuevo tipo de permiso" onClose={() => setCreating(false)}>
          <CreateTypeForm
            create={create}
            onCancel={() => setCreating(false)}
            onCreated={(type) => {
              setCreating(false);
              setSaved(`Tipo «${type.name}» creado.`);
            }}
          />
        </Modal>
      ) : null}

      {editing ? (
        <Modal title={`Editar «${editing.name}»`} onClose={() => setEditingId(undefined)}>
          <EditTypeForm
            // Una versión nueva (tras un 412) recarga el formulario; la ventana sigue abierta.
            key={`${editing.id}-${editing.version}`}
            type={editing}
            update={update}
            onClose={() => setEditingId(undefined)}
            onSaved={(type) => {
              setEditingId(undefined);
              setSaved(`Cambios guardados en «${type.name}».`);
            }}
          />
        </Modal>
      ) : null}
    </div>
  );
}

interface TypeDraft {
  code: string;
  name: string;
  description: string;
  allowsDocument: boolean;
  requiresDocument: boolean;
}

function TypeFields({ prefix, draft, set, errors }: { prefix: string; draft: TypeDraft; set: (next: Partial<TypeDraft>) => void; errors: TypeErrors }) {
  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field id={`${prefix}-code`} label="Código" error={errors.code}>
          <input id={`${prefix}-code`} value={draft.code} onChange={(event) => set({ code: event.target.value })} className={INPUT} {...describedBy(`${prefix}-code`, errors.code)} />
        </Field>
        <Field id={`${prefix}-name`} label="Nombre" error={errors.name}>
          <input id={`${prefix}-name`} value={draft.name} onChange={(event) => set({ name: event.target.value })} className={INPUT} {...describedBy(`${prefix}-name`, errors.name)} />
        </Field>
      </div>
      <Field id={`${prefix}-description`} label="Descripción" error={errors.description}>
        <textarea
          id={`${prefix}-description`}
          rows={2}
          value={draft.description}
          onChange={(event) => set({ description: event.target.value })}
          className={INPUT}
          {...describedBy(`${prefix}-description`, errors.description)}
        />
      </Field>
      <Checkbox id={`${prefix}-allows`} label="Permite documento" checked={draft.allowsDocument} onChange={(value) => set({ allowsDocument: value })} />
      <Checkbox id={`${prefix}-requires`} label="Requiere documento" checked={draft.requiresDocument} onChange={(value) => set({ requiresDocument: value })} />
      {errors.requiresDocument ? (
        <p id={`${prefix}-requires-error`} className="text-sm text-red-700">
          {errors.requiresDocument}
        </p>
      ) : null}
    </>
  );
}

const EMPTY_DRAFT: TypeDraft = { code: '', name: '', description: '', allowsDocument: false, requiresDocument: false };

function CreateTypeForm({
  create,
  onCancel,
  onCreated,
}: {
  create: ReturnType<typeof useCreateType>;
  onCancel: () => void;
  /** Tras el alta se cierra la ventana y la página muestra «Tipo «{nombre}» creado.». */
  onCreated: (type: PermissionType) => void;
}) {
  const [draft, setDraft] = useState<TypeDraft>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<TypeErrors>({});

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsed = PermissionTypeCreateSchema.safeParse({ ...draft, description: nullIfBlank(draft.description) });
    if (!parsed.success) {
      setErrors(shapeErrors(parsed.error.issues));
      return;
    }
    setErrors({});
    create.mutate(parsed.data, { onSuccess: onCreated });
  };

  return (
    <form noValidate onSubmit={submit} className="space-y-3" aria-label="Nuevo tipo de permiso">
      {create.error ? <ProblemMessage error={create.error} /> : null}
      <TypeFields prefix="type" draft={draft} set={(next) => setDraft((current) => ({ ...current, ...next }))} errors={errors} />
      <ModalFooter>
        <button type="button" className={OUTLINE} onClick={onCancel}>
          Cancelar
        </button>
        <button type="submit" disabled={create.isPending} className={PRIMARY}>
          Crear tipo
        </button>
      </ModalFooter>
    </form>
  );
}

function EditTypeForm({
  type,
  update,
  onClose,
  onSaved,
}: {
  type: PermissionType;
  update: ReturnType<typeof useUpdateType>;
  onClose: () => void;
  onSaved: (type: PermissionType) => void;
}) {
  const [draft, setDraft] = useState<TypeDraft>({
    code: type.code,
    name: type.name,
    description: type.description ?? '',
    allowsDocument: type.allowsDocument,
    requiresDocument: type.requiresDocument,
  });
  const [active, setActive] = useState(type.active);
  const [errors, setErrors] = useState<TypeErrors>({});
  const [notice, setNotice] = useState<string | undefined>(undefined);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const changes: PermissionTypeUpdate = {};
    if (draft.code !== type.code) changes.code = draft.code;
    if (draft.name !== type.name) changes.name = draft.name;
    if (nullIfBlank(draft.description) !== type.description) changes.description = nullIfBlank(draft.description);
    if (draft.allowsDocument !== type.allowsDocument) changes.allowsDocument = draft.allowsDocument;
    if (draft.requiresDocument !== type.requiresDocument) changes.requiresDocument = draft.requiresDocument;
    if (active !== type.active) changes.active = active;
    if (Object.keys(changes).length === 0) {
      setNotice('No hay cambios que guardar.');
      return;
    }
    // La coherencia de «requiere» y «permite» documento con el estado final la decide la API (409/422).
    const parsed = PermissionTypeUpdateSchema.safeParse(changes);
    if (!parsed.success) {
      setErrors(shapeErrors(parsed.error.issues));
      return;
    }
    setErrors({});
    setNotice(undefined);
    update.mutate({ id: type.id, version: type.version, changes: parsed.data }, { onSuccess: onSaved });
  };

  return (
    <form noValidate onSubmit={submit} className="space-y-3" aria-label={`Editar ${type.name}`}>
      <Notice text={notice} />
      {update.error ? (
        <>
          {isStale(update.error) ? <Notice text="Otra persona cambió el tipo. Se cargaron los datos actuales: revísalos y vuelve a guardar." /> : null}
          <ProblemMessage error={update.error} />
        </>
      ) : null}
      <TypeFields prefix="edit-type" draft={draft} set={(next) => setDraft((current) => ({ ...current, ...next }))} errors={errors} />
      <Checkbox id="edit-type-active" label="Activo" checked={active} onChange={setActive} />
      <ModalFooter>
        <button type="button" className={OUTLINE} onClick={onClose}>
          Cancelar
        </button>
        <button type="submit" disabled={update.isPending} className={PRIMARY}>
          Guardar cambios
        </button>
      </ModalFooter>
    </form>
  );
}
